import type {
  ImageDimensions,
  ImageEdit,
  ImageRotation,
  NormalizedImagePoint,
} from './imageEditorGeometry'

export type ImageEditTransformValue = Pick<
  ImageEdit,
  'flipHorizontal' | 'flipVertical' | 'rotation' | 'straightening'
>

export type ImageEditTransformMatrix = Readonly<{
  a: number
  b: number
  c: number
  d: number
  e: number
  f: number
}>

export type ImageEditTransform = Readonly<{
  coverScale: number
  rotationDegrees: number
  sourceDimensions: ImageDimensions
  sourceToStage: ImageEditTransformMatrix
  stageDimensions: ImageDimensions
  stageToSource: ImageEditTransformMatrix
}>

export const IMAGE_EDITOR_MIN_STRAIGHTENING = -45
export const IMAGE_EDITOR_MAX_STRAIGHTENING = 45

const assertPositiveFiniteNumber = (value: number, name: string): void => {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`)
  }
}

const assertSourceDimensions = (dimensions: ImageDimensions): void => {
  assertPositiveFiniteNumber(dimensions.width, 'sourceDimensions.width')
  assertPositiveFiniteNumber(dimensions.height, 'sourceDimensions.height')
}

const assertStraightening = (straightening: number): void => {
  if (
    !Number.isInteger(straightening) ||
    straightening < IMAGE_EDITOR_MIN_STRAIGHTENING ||
    straightening > IMAGE_EDITOR_MAX_STRAIGHTENING
  ) {
    throw new RangeError(
      'straightening must be an integer between -45 and 45 degrees',
    )
  }
}

const getQuarterTurnTransform = (
  sourceDimensions: ImageDimensions,
  rotation: ImageRotation,
): Readonly<{
  matrix: ImageEditTransformMatrix
  stageDimensions: ImageDimensions
}> => {
  const { width, height } = sourceDimensions

  switch (rotation) {
    case 0:
      return {
        matrix: { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 },
        stageDimensions: { width, height },
      }
    case 90:
      return {
        matrix: { a: 0, b: 1, c: -1, d: 0, e: height, f: 0 },
        stageDimensions: { width: height, height: width },
      }
    case 180:
      return {
        matrix: { a: -1, b: 0, c: 0, d: -1, e: width, f: height },
        stageDimensions: { width, height },
      }
    case 270:
      return {
        matrix: { a: 0, b: -1, c: 1, d: 0, e: 0, f: width },
        stageDimensions: { width: height, height: width },
      }
  }
}

const getCoverScale = (
  stageDimensions: ImageDimensions,
  radians: number,
): number => {
  const cosine = Math.abs(Math.cos(radians))
  const sine = Math.abs(Math.sin(radians))
  const { width, height } = stageDimensions

  return Math.max(
    cosine + (height / width) * sine,
    cosine + (width / height) * sine,
  )
}

const getOrientedToStageTransform = (
  stageDimensions: ImageDimensions,
  edit: ImageEditTransformValue,
  radians: number,
  coverScale: number,
): ImageEditTransformMatrix => {
  const horizontalDirection = edit.flipHorizontal ? -1 : 1
  const verticalDirection = edit.flipVertical ? -1 : 1
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)
  const a = horizontalDirection * coverScale * cosine
  const b = verticalDirection * coverScale * sine
  const c = -horizontalDirection * coverScale * sine
  const d = verticalDirection * coverScale * cosine
  const centerX = stageDimensions.width / 2
  const centerY = stageDimensions.height / 2

  return {
    a,
    b,
    c,
    d,
    e: centerX - a * centerX - c * centerY,
    f: centerY - b * centerX - d * centerY,
  }
}

const composeTransforms = (
  outer: ImageEditTransformMatrix,
  inner: ImageEditTransformMatrix,
): ImageEditTransformMatrix => ({
  a: outer.a * inner.a + outer.c * inner.b,
  b: outer.b * inner.a + outer.d * inner.b,
  c: outer.a * inner.c + outer.c * inner.d,
  d: outer.b * inner.c + outer.d * inner.d,
  e: outer.a * inner.e + outer.c * inner.f + outer.e,
  f: outer.b * inner.e + outer.d * inner.f + outer.f,
})

const invertTransform = (
  transform: ImageEditTransformMatrix,
): ImageEditTransformMatrix => {
  const determinant = transform.a * transform.d - transform.b * transform.c

  return {
    a: transform.d / determinant,
    b: -transform.b / determinant,
    c: -transform.c / determinant,
    d: transform.a / determinant,
    e: (transform.c * transform.f - transform.d * transform.e) / determinant,
    f: (transform.b * transform.e - transform.a * transform.f) / determinant,
  }
}

const applyTransform = (
  transform: ImageEditTransformMatrix,
  point: NormalizedImagePoint,
): NormalizedImagePoint => ({
  x: transform.a * point.x + transform.c * point.y + transform.e,
  y: transform.b * point.x + transform.d * point.y + transform.f,
})

export const createImageEditTransform = (
  sourceDimensions: ImageDimensions,
  edit: ImageEditTransformValue,
): ImageEditTransform => {
  assertSourceDimensions(sourceDimensions)
  assertStraightening(edit.straightening)

  const quarterTurn = getQuarterTurnTransform(sourceDimensions, edit.rotation)
  const radians = (edit.straightening * Math.PI) / 180
  const coverScale = getCoverScale(quarterTurn.stageDimensions, radians)
  const orientedToStage = getOrientedToStageTransform(
    quarterTurn.stageDimensions,
    edit,
    radians,
    coverScale,
  )
  const sourceToStage = composeTransforms(orientedToStage, quarterTurn.matrix)

  return {
    coverScale,
    rotationDegrees: edit.rotation + edit.straightening,
    sourceDimensions,
    sourceToStage,
    stageDimensions: quarterTurn.stageDimensions,
    stageToSource: invertTransform(sourceToStage),
  }
}

export const projectSourcePointToStage = (
  point: NormalizedImagePoint,
  transform: ImageEditTransform,
): NormalizedImagePoint => {
  const sourcePoint = {
    x: point.x * transform.sourceDimensions.width,
    y: point.y * transform.sourceDimensions.height,
  }
  const stagePoint = applyTransform(transform.sourceToStage, sourcePoint)

  return {
    x: stagePoint.x / transform.stageDimensions.width,
    y: stagePoint.y / transform.stageDimensions.height,
  }
}

export const unprojectStagePointToSource = (
  point: NormalizedImagePoint,
  transform: ImageEditTransform,
): NormalizedImagePoint => {
  const stagePoint = {
    x: point.x * transform.stageDimensions.width,
    y: point.y * transform.stageDimensions.height,
  }
  const sourcePoint = applyTransform(transform.stageToSource, stagePoint)

  return {
    x: sourcePoint.x / transform.sourceDimensions.width,
    y: sourcePoint.y / transform.sourceDimensions.height,
  }
}

export const getImageEditCssTransform = (
  sourceDimensions: ImageDimensions,
  edit: ImageEditTransformValue,
): string => {
  const transform = createImageEditTransform(sourceDimensions, edit)
  const coverTransform =
    transform.coverScale === 1 ? '' : ` scale(${transform.coverScale})`

  return `translate(-50%, -50%) scaleX(${edit.flipHorizontal ? -1 : 1}) scaleY(${edit.flipVertical ? -1 : 1})${coverTransform} rotate(${transform.rotationDegrees}deg)`
}
